import { useOrganisationList } from "@inrange/building-manager-api-client";
import { Organisation } from "@inrange/building-manager-api-client/models-organisation";
import { SiteOwnership } from "@inrange/building-manager-api-client/models-site";
import { Icons } from "@inrange/theme-components/icons";
import { sortBy, sortByAdditional } from "@inrange/utils";
import { useState } from "react";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import { Link } from "react-router-dom";
import getCustomerAppDomain from "../../../../utils/getCustomerAppDomain";
import { SITE_ORG_LABELS } from "../enums";

const BelongsTo = ({
  siteID,
  ownerships,
  onOwnerEdited,
  onOwnerRemoved,
  setShowBelongsTo,
}: {
  siteID: string;
  ownerships: SiteOwnership[];
  onOwnerEdited: React.Dispatch<
    React.SetStateAction<Record<string, SiteOwnership>>
  >;
  onOwnerRemoved: (owner: { orgID: string; ownership: string }) => void;
  setShowBelongsTo: (show: boolean) => void;
}) => {
  const { fetchOrganisations } = useOrganisationList();
  const [siteOwnerships, setSiteOwnerships] = useState([...ownerships]);

  const removeOwnership = (siteOrg) => {
    const updatedExistingOwners = siteOwnerships.filter(
      (existingSiteOrg) => existingSiteOrg.orgID !== siteOrg.orgID
    );
    if (updatedExistingOwners.length === 0) {
      setShowBelongsTo(false);
    }
    onOwnerRemoved(siteOrg);
    setSiteOwnerships(updatedExistingOwners);
  };

  const orgsById = (fetchOrganisations.data?.organisations || []).reduce(
    (acc: Record<string, Organisation>, org: Organisation) => {
      acc[org.id] = org;
      return acc;
    },
    {}
  );

  return (
    <Row style={{ padding: "0 12px" }}>
      <Form.Text muted>Belongs to:</Form.Text>
      <Table
        borderless
        style={{
          margin: "0 12px 16px 12px",
          width: "calc(100% - 24px)",
        }}
      >
        <tbody>
          {siteOwnerships
            .sort(
              sortByAdditional(
                (siteOrg) => siteOrg.name,
                sortBy((siteOrg) => siteOrg.ownership)
              )
            )
            .map((siteOrg, i) => (
              <BelongsToRow
                key={i}
                index={i}
                siteID={siteID}
                siteOrg={siteOrg}
                org={orgsById[siteOrg.orgID]}
                onOwnerEdited={onOwnerEdited}
                onOwnerRemoved={removeOwnership}
              />
            ))}
        </tbody>
      </Table>
    </Row>
  );
};

const BelongsToRow = ({
  index,
  siteID,
  siteOrg,
  org,
  onOwnerEdited,
  onOwnerRemoved,
}: {
  index: number;
  siteID: string;
  siteOrg: SiteOwnership;
  org?: Organisation;
  onOwnerEdited: React.Dispatch<
    React.SetStateAction<Record<string, SiteOwnership>>
  >;
  onOwnerRemoved: (owner: { orgID: string; ownership: string }) => void;
}) => {
  const [park, setPark] = useState(siteOrg.park);
  const [spv, setSpv] = useState(siteOrg.spv);
  return (
    <tr>
      <td style={{ verticalAlign: "middle" }}>
        <strong style={{ padding: 0 }}>
          <Link
            to={`/organisation/${siteOrg.orgID}/edit`}
            style={{ color: "black", textDecoration: "none" }}
            data-testid={`belongs-to-org-link-${index}`}
          >
            {siteOrg.name}
          </Link>
        </strong>
      </td>
      {siteOrg.ownership !== "existing" && (
        <td style={{ verticalAlign: "middle" }}>
          <div>{SITE_ORG_LABELS[siteOrg.ownership]}</div>
        </td>
      )}
      <td
        style={{ verticalAlign: "middle" }}
        colSpan={siteOrg.ownership === "funder" ? 2 : 1}
      >
        {org?.parks && org?.parks?.length > 0 && (
          <Form.Group controlId="parkEdit">
            <Form.Select
              value={park ?? ""}
              onChange={(e) => {
                setPark(e.target.value);
                onOwnerEdited((previous) => {
                  return {
                    ...previous,
                    [siteOrg.orgID]: {
                      ...siteOrg,
                      park: e.target.value,
                    },
                  };
                });
              }}
            >
              <option value="">No park</option>
              {org.parks.sort().map((park) => (
                <option key={park} value={park}>
                  {park}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}
        {org?.spvs && org?.spvs?.length > 0 && (
          <Form.Group controlId="spvEdit">
            <Form.Select
              value={spv ?? ""}
              onChange={(e) => {
                setSpv(e.target.value);
                onOwnerEdited((previous) => {
                  return {
                    ...previous,
                    [siteOrg.orgID]: {
                      ...siteOrg,
                      spv: e.target.value,
                    },
                  };
                });
              }}
            >
              <option value="">No SPV</option>
              {org.spvs
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((spv) => (
                  <option key={spv.uuid} value={spv.uuid}>
                    {spv.name}
                  </option>
                ))}
            </Form.Select>
          </Form.Group>
        )}
      </td>
      {siteOrg.ownership !== "funder" && (
        <td style={{ verticalAlign: "middle" }}>
          <a
            href={`${getCustomerAppDomain()}/org/${siteOrg.orgID}/site/${siteID}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#0d6efd",
              textDecoration: "none",
              cursor: "pointer",
            }}
            data-testid={`customer-app-site-link-${index}`}
          >
            Public site
            <img
              alt="view"
              src={Icons.shareBlue}
              style={{
                width: "17px",
                height: "17px",
                marginLeft: "8px",
                marginBottom: "2px",
                cursor: "pointer",
              }}
            ></img>
          </a>
        </td>
      )}
      <td
        style={{
          verticalAlign: "middle",
          textAlign: "right",
        }}
      >
        <div
          onClick={() => onOwnerRemoved(siteOrg)}
          style={{
            cursor: "pointer",
          }}
        >
          <img
            data-testid={`remove-owner-${index}`}
            alt="remove"
            src={Icons.trashcanRed}
            style={{
              width: "20px",
              height: "20px",
              marginLeft: "6px",
              cursor: "pointer",
            }}
          ></img>
        </div>
      </td>
    </tr>
  );
};

export default BelongsTo;
